/** Cheap home layout prefs — plan/50 (show/hide + order; no drag-resize). */

export const HOME_MAIN_WIDGET_IDS = [
  "salesTrend",
  "collectAging",
  "act",
  "recent",
  "today",
  "more",
] as const;

export type HomeMainWidgetId = (typeof HOME_MAIN_WIDGET_IDS)[number];

export type HomeWidgetPrefs = {
  order: HomeMainWidgetId[];
  hidden: HomeMainWidgetId[];
};

export const HOME_WIDGET_LABELS: Record<HomeMainWidgetId, string> = {
  salesTrend: "Sales trend",
  collectAging: "To collect aging",
  act: "Act",
  recent: "Recent",
  today: "Today",
  more: "More",
};

function prefsKey(companySlug: string): string {
  return `icraftx.homeWidgets.v1:${companySlug}`;
}

export function defaultHomeWidgetPrefs(): HomeWidgetPrefs {
  return {
    order: [...HOME_MAIN_WIDGET_IDS],
    hidden: [],
  };
}

/** Merge stored prefs with catalog (new widgets append; unknown ids dropped). */
export function normalizeHomeWidgetPrefs(
  raw: Partial<HomeWidgetPrefs> | null | undefined,
): HomeWidgetPrefs {
  const defaults = defaultHomeWidgetPrefs();
  const orderIn = Array.isArray(raw?.order) ? raw!.order : [];
  const hiddenIn = Array.isArray(raw?.hidden) ? raw!.hidden : [];
  const seen = new Set<HomeMainWidgetId>();
  const order: HomeMainWidgetId[] = [];
  for (const id of orderIn) {
    if (
      HOME_MAIN_WIDGET_IDS.includes(id as HomeMainWidgetId) &&
      !seen.has(id as HomeMainWidgetId)
    ) {
      order.push(id as HomeMainWidgetId);
      seen.add(id as HomeMainWidgetId);
    }
  }
  for (const id of HOME_MAIN_WIDGET_IDS) {
    if (!seen.has(id)) order.push(id);
  }
  const hidden = hiddenIn.filter(
    (id): id is HomeMainWidgetId =>
      HOME_MAIN_WIDGET_IDS.includes(id as HomeMainWidgetId),
  );
  return { order, hidden: [...new Set(hidden)] };
}

export function loadHomeWidgetPrefs(companySlug: string): HomeWidgetPrefs {
  if (typeof localStorage === "undefined" || !companySlug) {
    return defaultHomeWidgetPrefs();
  }
  try {
    const raw = localStorage.getItem(prefsKey(companySlug));
    if (!raw) return defaultHomeWidgetPrefs();
    return normalizeHomeWidgetPrefs(
      JSON.parse(raw) as Partial<HomeWidgetPrefs>,
    );
  } catch {
    return defaultHomeWidgetPrefs();
  }
}

export function saveHomeWidgetPrefs(
  companySlug: string,
  prefs: HomeWidgetPrefs,
): void {
  if (typeof localStorage === "undefined" || !companySlug) return;
  try {
    localStorage.setItem(
      prefsKey(companySlug),
      JSON.stringify(normalizeHomeWidgetPrefs(prefs)),
    );
  } catch {
    /* quota / private mode */
  }
}

export function visibleHomeWidgetOrder(
  prefs: HomeWidgetPrefs,
): HomeMainWidgetId[] {
  const hidden = new Set(prefs.hidden);
  return prefs.order.filter((id) => !hidden.has(id));
}

export function toggleHomeWidgetHidden(
  prefs: HomeWidgetPrefs,
  id: HomeMainWidgetId,
): HomeWidgetPrefs {
  const hidden = new Set(prefs.hidden);
  if (hidden.has(id)) hidden.delete(id);
  else hidden.add(id);
  return { ...prefs, hidden: [...hidden] };
}

export function moveHomeWidget(
  prefs: HomeWidgetPrefs,
  id: HomeMainWidgetId,
  dir: -1 | 1,
): HomeWidgetPrefs {
  const order = [...prefs.order];
  const i = order.indexOf(id);
  if (i < 0) return prefs;
  const j = i + dir;
  if (j < 0 || j >= order.length) return prefs;
  const tmp = order[i]!;
  order[i] = order[j]!;
  order[j] = tmp;
  return { ...prefs, order };
}
